import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import DataTable from "@shared-ui-components/DataTable";

import LocationContext from "@context/location/locationContext";
import { Job, LabourStore } from "@shared-ui-components/DataTable/types";

import SettingsContext from "@context/Settings/settingsContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { message } from "@src/constants";
import { SORTKEYMAP } from "@src/constants/DataTable";
import { getApiResponse } from "@src/utils/service";
import {
  columns,
  filterOptions,
  getLabourBreakdownCustomExtraPath,
  refineData,
} from "./helper";

const LabourBreakdownTable = ({ pagination }: { pagination: boolean }) => {
  const [type, setType] = useState("");
  const [dyColumns, setDyColumns] = useState(columns);
  const [isLoading, setIsLoading] = useState(true);
  const [rowData, setRowsData] = useState<LabourStore[] | Job[]>([]);
  const [typeOptions, setTypeOptions] = useState([
    { label: "By Job Code", value: "job_code" },
  ]);

  const [pageNumber, setPageNumber] = useState(0);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState({
    tableName: "amt_act",
    label: "AMT $",
    order: "desc",
  });

  const [{ locationState }] = useContext(LocationContext);

  const { property, store, revenueCenter, date } = locationState;

  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);

  const [{ settingsState }] = useContext(SettingsContext);

  const { planData, foreCastData } = settingsState;

  const filterType = filterOptions.find((el) => el.label === type)?.value;

  const filters = {
    property,
    store,
    rvc: revenueCenter,
    date,
    filterBy: filterType || "",
    planData,
    foreCastData,
  };

  const filter = getLabourBreakdownCustomExtraPath(filters);

  const url = `labour-breakdown${filter}&sort_by=${
    sortBy.tableName
  }&sort_order=${sortBy.order === "desc" ? "desc" : "asc"}&page=${
    pageNumber + 1
  }`;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPageNumber(newPage - 1);
  };

  const setLocalFilter = (changedtype: string) => {
    localStorage.setItem(
      "LabourFilter",
      JSON.stringify({ filtertype: changedtype })
    );
    setType(changedtype);
  };

  const handleDyColumns = (changedType: string) => {
    setDyColumns((prev) => {
      const newColumns = prev.filter(
        (column) => column.label !== "Job" && column.label !== "Store"
      );
      return [
        {
          id: "name",
          label: changedType?.toLowerCase() === "by store" ? "Store" : "Job",
          minWidth: 110,
        },
        ...newColumns,
      ];
    });
  };

  const localRevenueFilter = localStorage.getItem("LabourFilter");

  const handleTypeChange = (changedType: string) => {
    setRowsData([]);
    setIsLoading(true);
    setPageNumber(0);
    handleDyColumns(changedType);
    setType(changedType);
    setLocalFilter(changedType);
  };

  const sortHandler = (sortkey: string) => {
    const sortKeyObject = SORTKEYMAP.find(
      (el) => el.tableName === sortkey.toLowerCase()
    );
    setSortBy((prev) => {
      return {
        tableName: sortKeyObject?.label || "name",
        label: sortkey,
        order: prev.order === "desc" ? "asc" : "desc",
      };
    });
  };

  const handleNavigate = () => {
    navigate("/labour-dashboard/labour-breakdown");
  };

  const handleLocalFilters = () => {
    if (localRevenueFilter !== null) {
      const localFilters = JSON.parse(localRevenueFilter);
      const localFilterType =
        localFilters.filtertype.length > 0
          ? localFilters.filtertype
          : "By Job Code";
      setType(localFilterType);
      handleDyColumns(localFilterType);

      if (property.length > 0 && store === "" && revenueCenter === "") {
        setTypeOptions((prev) => {
          return [...prev, { label: "By Store", value: "store" }];
        });
      }
      if (property.length > 0 && store.length > 0 && revenueCenter === "") {
        if (localFilters.filtertype === "") {
          setLocalFilter("By Job Code");
        }
        if (localFilters.filtertype === "By Store") {
          setType("By Job Code");
        }
      } else if (property.length && store.length && revenueCenter.length) {
        if (localFilters.filtertype === "") {
          setLocalFilter("By Job Code");
        }
        if (localFilters.filtertype === "By Store") {
          setType("By Job Code");
        }
      }
    } else {
      setType("By Job Code");
      setLocalFilter(type);
    }
  };

  useEffect(() => {
    setPageNumber(0);
    handleLocalFilters();
    return () => {
      setTypeOptions([{ label: "By Job Code", value: "job_code" }]);
    };
  }, [property, store, revenueCenter, date, localRevenueFilter]);

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const result = await getApiResponse(url);
        if (result?.data !== null) {
          setRowsData(result?.data);
          setIsLoading(false);
        } else {
          showErrorToastSetState({
            errorMessage: result?.error || "",
            errorTitle: message.ERROR_MESSAGE_DASHBOARD,
            open: true,
          });
        }
        setIsLoading(false);
      } catch (err) {
        showErrorToastSetState({
          errorMessage: "",
          errorTitle: message.ERROR_MESSAGE_DASHBOARD,
          open: true,
        });
      }
    };
    if (filterType !== undefined) {
      getData();
    } else {
      setIsLoading(false);
    }
  }, [url, filterType]);

  return (
    <DataTable
      sortHandler={sortHandler}
      title="Labour Breakdown"
      rowData={refineData(rowData)}
      page={pageNumber}
      showPagination={pagination}
      sortBy={sortBy}
      handleNavigate={handleNavigate}
      handlePageChange={handleChangePage}
      handleTypeChange={handleTypeChange}
      type={type}
      isLoading={isLoading}
      dyColumns={dyColumns}
      dropDownOptions={typeOptions}
    />
  );
};

export default LabourBreakdownTable;
